// maths.portll.net — Completion flow (plan §1.6).
//
// A single warm sheet, not a nagging wizard. Steps stack top-to-bottom:
//   (1) attestation — "We did this together" — the ONE required tick (the honesty
//       contract the server rules also enforce),
//   (2) the "little wins" rubric (optional),
//   (3) keepsake photos (optional, EXIF-stripped in the component),
//   (4) if the task carries a datasetSpec, type in what was collected — or defer with
//       "We'll type it in later".
//
// Finish order matters: we pre-mint the completion id so photos upload UNDER it first,
// then (if a valid dataset was entered) saveDataset, then logCompletion with the same
// id. Saving the child's data is what powers the payoff — "new questions about their
// data are waiting in their app" — sized by buildYourDataPack. The attestation record
// is the load-bearing artefact, so a wobbly dataset never blocks it.

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase.js";
import { fetchPublishedTasksForYear, newCompletionRef, logCompletion, saveDataset } from "../lib/tasks.js";
import { emptyDataset, validateDataset } from "../lib/datasetShapes.js";
import { buildYourDataPack } from "../engine/index.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useProfiles } from "../contexts/ProfileContext.jsx";
import DatasetEntry from "../components/DatasetEntry.jsx";
import RubricTicks from "../components/RubricTicks.jsx";
import PhotoUpload from "../components/PhotoUpload.jsx";
import { PageHeader, Card, Button, Note, Spinner, Chip, EmptyState, injectStyles } from "../components/ui.jsx";

function StepHeader({ n, title, optional }) {
  return (
    <div className="cf-step__head">
      <span className="cf-step__n" aria-hidden="true">
        {n}
      </span>
      <h2 className="cf-step__title">{title}</h2>
      {optional && <Chip tone="neutral">optional</Chip>}
    </div>
  );
}

export default function CompletionFlow() {
  const { childId, taskId } = useParams();
  const { user } = useAuth();
  const { profiles, displayNameFor, loading: profilesLoading } = useProfiles();
  const uid = user?.uid || null;

  const child = profiles.find((p) => p.id === childId) || null;

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Step state
  const [attested, setAttested] = useState(false);
  const [rubricTicks, setRubricTicks] = useState([]);
  const [photos, setPhotos] = useState([]); // storage paths
  const [datasetMode, setDatasetMode] = useState("now"); // 'now' | 'later'
  const [dataset, setDataset] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { datasetId, pack } → payoff screen

  const base = `/grown-ups/child/${childId}/task/${taskId}`;

  // Pre-mint the completion ref ONCE so photos can upload under its id before the doc
  // is written. Reused verbatim by logCompletion at finish.
  const mintRef = useRef(null);
  if (uid && childId && !mintRef.current) mintRef.current = newCompletionRef(uid, childId);
  const completionId = mintRef.current ? mintRef.current.id : null;

  // Load the task (published-for-year first, then a direct getDoc fallback).
  useEffect(() => {
    if (profilesLoading) return;
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        let found = null;
        if (child && child.yearLevel != null) {
          const list = await fetchPublishedTasksForYear(child.yearLevel);
          found = list.find((t) => t.id === taskId) || null;
        }
        if (!found) {
          const snap = await getDoc(doc(db, `tasks/${taskId}`));
          if (snap.exists()) found = { id: snap.id, ...snap.data() };
        }
        if (!alive) return;
        setTask(found);
        setNotFound(!found);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [taskId, child, profilesLoading]);

  // Seed the dataset payload once the task (and its spec) is known.
  useEffect(() => {
    if (task && task.datasetSpec && dataset == null) {
      setDataset(emptyDataset(task.datasetSpec.kind, task.datasetSpec));
    }
  }, [task, dataset]);

  const childName = (child ? displayNameFor(child) : "") || "your child";

  async function handleFinish() {
    if (!attested || saving || !uid) return;
    setSaving(true);
    setError(null);
    try {
      // Reuse the pre-minted id (photos already live under it); mint late only if needed.
      let cid = completionId;
      if (!cid) {
        mintRef.current = newCompletionRef(uid, childId);
        cid = mintRef.current.id;
      }

      const spec = task.datasetSpec;
      let datasetId;
      let pack = null;
      if (spec && datasetMode === "now" && dataset && validateDataset(spec.kind, dataset).ok) {
        try {
          datasetId = await saveDataset(uid, childId, {
            kind: spec.kind,
            name: task.title,
            data: dataset,
            taskId,
            collectedOn: new Date().toISOString().slice(0, 10),
          });
          pack = buildYourDataPack({
            kind: spec.kind,
            data: dataset,
            generatorHints: task.generatorHints,
          });
        } catch (e) {
          // The attestation is what matters — keep going without the dataset.
          console.error("saveDataset failed; logging completion without data", e);
          datasetId = undefined;
          pack = null;
        }
      }

      await logCompletion(
        uid,
        childId,
        {
          taskId,
          taskVersion: task.version,
          attestation: true,
          rubricTicks,
          photoIds: photos,
          datasetId,
        },
        cid
      );

      setResult({ datasetId, pack });
    } catch (e) {
      setError(e?.message || "Something got in the way of saving that. Give it another go?");
    } finally {
      setSaving(false);
    }
  }

  // ── loading / not-found ──────────────────────────────────────────────────────
  if (loading || profilesLoading) {
    return (
      <main className="container cf-center">
        <Spinner size={30} label="Loading" />
      </main>
    );
  }
  if (notFound || !task) {
    return (
      <main className="container">
        <PageHeader title="We did it" back={`/grown-ups/child/${childId}`} />
        <EmptyState
          icon="🧭"
          title="We couldn't find that activity"
          hint="Head back and pick another to log."
          action={<Button variant="secondary" to={`/grown-ups/child/${childId}`}>Back to activities</Button>}
        />
      </main>
    );
  }

  // ── payoff screen ────────────────────────────────────────────────────────────
  if (result) {
    const savedData = !!result.datasetId;
    const pack = result.pack;
    const n = pack && Array.isArray(pack.questions) ? pack.questions.length : 0;
    return (
      <main className="container cf-payoff">
        <div className="cf-payoff__spark" aria-hidden="true">
          🎉
        </div>
        <h1 className="cf-payoff__title">That's explored together</h1>
        {savedData && pack && pack.enough ? (
          <p className="cf-payoff__lead">
            {n} new question{n === 1 ? "" : "s"} about {childName}'s data {n === 1 ? "is" : "are"} waiting in
            their app.
          </p>
        ) : savedData ? (
          <p className="cf-payoff__lead">
            Saved. {pack && pack.reason ? pack.reason : "Collect a few more and new questions will appear in their app."}
          </p>
        ) : (
          <p className="cf-payoff__lead">Lovely work — that one's in {childName}'s explored list now.</p>
        )}
        <div className="cf-payoff__actions">
          <Button variant="primary" size="lg" to={`/grown-ups/child/${childId}`}>
            Back to {childName}'s activities
          </Button>
          {savedData && pack && pack.enough && (
            <Button variant="secondary" size="md" to="/app">
              Open the kids' app
            </Button>
          )}
        </div>
      </main>
    );
  }

  // ── the sheet ────────────────────────────────────────────────────────────────
  const showRubric = Array.isArray(task.rubric) && task.rubric.length > 0;
  const showDataset = !!task.datasetSpec;

  let step = 0;
  const attestN = ++step;
  const rubricN = showRubric ? ++step : null;
  const photoN = ++step;
  const datasetN = showDataset ? ++step : null;

  const photoHint =
    task.photoPolicy === "suggested"
      ? "A photo makes a lovely keepsake — totally up to you."
      : "Add a keepsake photo if you'd like — totally optional.";

  return (
    <main className="container cf-root">
      <PageHeader
        title="We did it"
        subtitle={task.title ? `Logging “${task.title}”` : undefined}
        back={base}
      />

      {/* (1) attestation — required */}
      <Card className={`cf-step cf-attest${attested ? " cf-attest--on" : ""}`}>
        <StepHeader n={attestN} title="We did this together" />
        <label className="cf-attest__box">
          <input
            type="checkbox"
            className="cf-attest__input"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
          />
          <span className="cf-attest__tick" aria-hidden="true">
            {attested ? "✓" : ""}
          </span>
          <span className="cf-attest__text">
            Yes — {childName} and I did this activity together.
            <span className="cf-attest__req"> (needed to save)</span>
          </span>
        </label>
      </Card>

      {/* (2) rubric — optional */}
      {rubricN != null && (
        <Card className="cf-step">
          <StepHeader n={rubricN} title="Little wins you noticed" optional />
          <RubricTicks rubric={task.rubric} value={rubricTicks} onChange={setRubricTicks} />
        </Card>
      )}

      {/* (3) photos — optional */}
      <Card className="cf-step">
        <StepHeader n={photoN} title="Add a photo" optional />
        <p className="cf-hint">{photoHint}</p>
        <PhotoUpload
          uid={uid}
          childId={childId}
          completionId={completionId}
          value={photos}
          onChange={setPhotos}
          max={3}
        />
      </Card>

      {/* (4) dataset — optional, only when the task collects data */}
      {datasetN != null && (
        <Card className="cf-step">
          <StepHeader n={datasetN} title="Type in what you collected" optional />
          {datasetMode === "now" ? (
            <>
              <DatasetEntry
                kind={task.datasetSpec.kind}
                spec={task.datasetSpec}
                value={dataset}
                onChange={setDataset}
              />
              <button type="button" className="cf-linkbtn" onClick={() => setDatasetMode("later")}>
                We'll type it in later →
              </button>
            </>
          ) : (
            <div className="cf-later">
              <Note tone="neutral">
                No worries — you can pop the numbers in from {childName}'s data screen whenever it suits. The new
                questions appear once the data's in.
              </Note>
              <Button variant="secondary" size="sm" onClick={() => setDatasetMode("now")}>
                Actually, let's type it now
              </Button>
            </div>
          )}
        </Card>
      )}

      {error && <Note tone="danger">{error}</Note>}

      {/* finish */}
      <div className="cf-finish">
        {!attested && (
          <p className="cf-finish__nudge" id="cf-finish-nudge">
            Tick “We did this together” to save.
          </p>
        )}
        <Button
          variant="primary"
          size="lg"
          className="cf-finish__btn"
          onClick={handleFinish}
          disabled={!attested || saving}
          aria-describedby={!attested ? "cf-finish-nudge" : undefined}
        >
          {saving ? (
            <>
              <Spinner size={20} label="Saving" /> Saving…
            </>
          ) : (
            <>
              <span aria-hidden="true">🌟</span> Save this
            </>
          )}
        </Button>
      </div>
    </main>
  );
}

const CF_CSS = `
.cf-center{display:grid;place-items:center;min-height:60vh;}
.cf-root{display:flex;flex-direction:column;gap:1rem;padding-bottom:3rem;}
.cf-hint{margin:0 0 .7rem;color:var(--ink-soft);font-size:.92rem;}

.cf-step__head{display:flex;align-items:center;gap:.6rem;margin-bottom:.85rem;}
.cf-step__n{flex:0 0 auto;display:inline-grid;place-items:center;width:30px;height:30px;border-radius:50%;
  background:color-mix(in srgb,var(--brand) 14%,#fff);color:var(--brand-ink);font-weight:800;font-size:.95rem;}
.cf-step__title{margin:0;font-size:1.05rem;flex:1 1 auto;}

.cf-attest{border:2px solid var(--line);transition:border-color .12s ease,background .12s ease;}
.cf-attest--on{border-color:color-mix(in srgb,var(--brand) 50%,#fff);background:color-mix(in srgb,var(--brand) 6%,#fff);}
.cf-attest__box{display:flex;align-items:center;gap:.8rem;min-height:56px;cursor:pointer;}
.cf-attest__input{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;}
.cf-attest__tick{flex:0 0 auto;display:inline-grid;place-items:center;width:34px;height:34px;border-radius:9px;
  border:2px solid color-mix(in srgb,var(--brand) 45%,#fff);background:#fff;color:#fff;font-weight:800;font-size:1.15rem;}
.cf-attest--on .cf-attest__tick{background:var(--brand);border-color:var(--brand);}
.cf-attest__box:focus-within .cf-attest__tick{outline:2px solid var(--sky);outline-offset:2px;}
.cf-attest__text{font-weight:600;line-height:1.3;}
.cf-attest__req{color:var(--ink-soft);font-weight:500;font-size:.88rem;}

.cf-linkbtn{margin-top:.85rem;padding:.4rem 0;background:none;border:0;color:var(--brand-ink);
  font-weight:700;text-decoration:underline;min-height:44px;}
.cf-later{display:flex;flex-direction:column;gap:.6rem;align-items:flex-start;}

.cf-finish{display:flex;flex-direction:column;gap:.5rem;margin-top:.5rem;}
.cf-finish__nudge{margin:0;color:var(--ink-soft);font-size:.9rem;}
.cf-finish__btn{width:100%;}

.cf-payoff{text-align:center;display:flex;flex-direction:column;align-items:center;gap:.6rem;
  padding-top:2.5rem;padding-bottom:3rem;}
.cf-payoff__spark{font-size:3rem;line-height:1;}
.cf-payoff__title{margin:.2rem 0 0;}
.cf-payoff__lead{margin:0;font-size:1.15rem;color:var(--ink);max-width:32ch;line-height:1.4;}
.cf-payoff__actions{display:flex;flex-wrap:wrap;gap:.6rem;justify-content:center;margin-top:1rem;}
`;

injectStyles("maths-completionflow-styles", CF_CSS);
